// frontend/src/components/PaymentStatus.jsx
// Landing page after PayU redirects back to the website.
// Backend surl/furl point here: /payment-status?id=<donationId>&status=success|failed&error=<msg>
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import { jsPDF } from 'jspdf';
import { toWords } from 'number-to-words';

const DESIGN_WIDTH  = Math.round(2482 / 4); // 621
const DESIGN_HEIGHT = Math.round(3508 / 4); // 877
const PDF_TEXT_OFFSET = 12;

const fields = [
  { key: 'receiptNumber', x: 479, y: 183 },
  { key: 'referenceId',   x: 160, y: 390 },
  { key: 'createdAt',     x: 103, y: 183 },
  { key: 'name',          x: 160, y: 230 },
  { key: 'whatsapp',      x: 160, y: 257 },
  { key: 'city',          x: 160, y: 284 },
  { key: 'note',          x: 160, y: 310 },
  { key: 'aadhar',        x: 160, y: 337 },
  { key: 'pan',           x: 399, y: 337 },
  { key: 'mode',          x: 160, y: 364 },
  { key: 'referenceId2',  x: 160, y: 390 },
  { key: 'amount',        x: 160, y: 417 },
  { key: 'amountInWords', x: 160, y: 444 },
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status     = searchParams.get('status');   // 'success' | 'failed'
  const donationId = searchParams.get('id');
  const errorMsg   = searchParams.get('error') || 'Payment could not be processed.';

  const [donation, setDonation]       = useState(null);
  const [sponsorUrl, setSponsorUrl]   = useState(null);
  const [receiptBg, setReceiptBg]     = useState('/images/reciept_image.jpg'); // default fallback
  const [scale, setScale]             = useState(1);
  const [fetchError, setFetchError]   = useState('');

  // Fetch donation data on success
  useEffect(() => {
    if (status === 'success' && donationId) {
      fetch(`${API_BASE}/api/donations/${donationId}`)
        .then(r => r.json())
        .then(data => {
          if (data.error) setFetchError(data.error);
          else setDonation(data);
        })
        .catch(() => setFetchError('Could not load donation details.'));
    }
  }, [status, donationId]);

  // Fetch sponsor image
  useEffect(() => {
    fetch(`${API_BASE}/api/sponsors/public`)
      .then(r => r.json())
      .then(sponsors => {
        if (sponsors.length > 0) setSponsorUrl(`${API_BASE}/uploads/sponsors/${sponsors[0].filename}`);
      })
      .catch(() => {});
  }, []);

  // Fetch receipt template for online donations
  useEffect(() => {
    fetch(`${API_BASE}/api/receipt-templates`)
      .then(r => r.json())
      .then(tpls => {
        if (tpls.online) setReceiptBg(`${API_BASE}/uploads/receiptTemplates/${tpls.online}`);
      })
      .catch(() => {});
  }, []);

  // Responsive scale for receipt preview
  useEffect(() => {
    const calc = () => {
      const maxW = window.innerWidth * 0.9;
      setScale(Math.min(maxW / DESIGN_WIDTH, 1));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const getAmountInWords = () => {
    if (!donation?.amount) return '';
    try { return `Rupees ${toWords(Number(donation.amount))} only`; }
    catch { return ''; }
  };

  const getFieldValue = (key) => {
    if (!donation) return '';
    switch (key) {
      case 'receiptNumber': return donation.receiptNumber || '';
      case 'referenceId':
      case 'referenceId2':  return donation.payu_txnid || donation.payment_id || '';
      case 'createdAt':     return formatDate(donation.createdAt);
      case 'name':          return donation.name || '';
      case 'whatsapp':      return donation.whatsapp || '';
      case 'note':          return donation.note || '';
      case 'aadhar':        return donation.aadharCard || '';
      case 'pan':           return donation.panCard || '';
      case 'mode':          return donation.payment_mode ? `Online / ${donation.payment_mode}` : 'Online';
      case 'amount':        return donation.amount ? String(donation.amount) : '';
      case 'amountInWords': return getAmountInWords();
      default:              return '';
    }
  };

  const handleDownloadPDF = () => {
    if (!donation) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'px', format: [DESIGN_WIDTH, DESIGN_HEIGHT] });
    const img = new Image();
    img.src = receiptBg;
    img.onload = () => {
      doc.addImage(img, 'JPG', 0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(17);
      doc.setTextColor(0, 0, 0);
      fields.forEach(f => {
        doc.text(getFieldValue(f.key), f.x, f.y + PDF_TEXT_OFFSET);
      });
      const finish = () => doc.save(`receipt_${donation.receiptNumber || 'donation'}.pdf`);
      if (sponsorUrl) {
        const sImg = new Image();
        sImg.src = sponsorUrl;
        sImg.onload = () => {
          doc.addImage(sImg, 'JPG', 0, DESIGN_HEIGHT - 200, DESIGN_WIDTH, 200);
          finish();
        };
        sImg.onerror = finish;
      } else {
        finish();
      }
    };
    img.onerror = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Could not load background image!', 50, 50);
      doc.save(`receipt_${donation.receiptNumber || 'donation'}.pdf`);
    };
  };

  // ── FAILURE VIEW ─────────────────────────────────────────────────────────────
  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-red-600 mb-2">Payment Failed</h1>
          <p className="text-gray-600 mb-6">{decodeURIComponent(errorMsg)}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-primary text-white py-2 rounded hover:bg-secondary transition-colors"
            >
              Try Again
            </button>
            <a
              href="mailto:kanaksuribhachau@gmail.com"
              className="w-full border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── SUCCESS VIEW ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">

        {/* Success banner */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 text-center">
          <div className="text-5xl mb-3">✅</div>
          <h1 className="text-2xl font-bold text-green-600 mb-1">Payment Successful!</h1>
          <p className="text-gray-600">
            Thank you for your generous donation to Shree Kanaksuri Ahinsadham.
          </p>
          {donation?.receiptNumber && (
            <p className="mt-2 font-semibold text-primary">
              Receipt No: {donation.receiptNumber}
            </p>
          )}
          {donation?.whatsapp && (
            <p className="text-sm text-gray-500 mt-1">
              A receipt has been sent to your WhatsApp ({donation.whatsapp}).
            </p>
          )}
        </div>

        {/* Loading / error states */}
        {!donation && !fetchError && (
          <p className="text-center text-gray-500">Loading receipt…</p>
        )}
        {fetchError && (
          <p className="text-center text-red-500">{fetchError}</p>
        )}

        {/* Receipt preview */}
        {donation && (
          <>
            <h2 className="text-xl font-bold mb-4 text-center">Donation Receipt</h2>

            <div
              style={{
                width: DESIGN_WIDTH,
                height: DESIGN_HEIGHT,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                marginBottom: DESIGN_HEIGHT * (scale - 1) + 16,
                border: '1px solid #ccc',
                boxShadow: '0 0 8px rgba(0,0,0,0.3)',
                position: 'relative',
                backgroundImage: `url("${receiptBg}")`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
              }}
            >
              {fields.map(f => (
                <div
                  key={f.key}
                  style={{
                    position: 'absolute',
                    top: f.y,
                    left: f.x,
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#000',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getFieldValue(f.key)}
                </div>
              ))}

              {sponsorUrl && (
                <img
                  src={sponsorUrl}
                  alt="Sponsor"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                  }}
                />
              )}
            </div>

            <div className="flex gap-4 mt-4 justify-center">
              <button
                onClick={handleDownloadPDF}
                className="bg-primary text-white px-6 py-2 rounded hover:bg-secondary transition-colors"
              >
                Download PDF
              </button>
              <button
                onClick={() => navigate('/')}
                className="border border-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-50 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;
